import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { clipCollectionQueryOptions, useClipCollection, type OlderPages } from '~/entities/exercise-clip';
import { useSessionUser } from '~/entities/session';
import { isGone } from '~/shared/api/mutator';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { buildRecordings, type RecordingsView } from '../model/recordings';

export interface RecordingsState {
  /** The first page of this narrowing has no answer yet — including a switch of client still showing the last. */
  readonly wallPending: boolean;
  /** The first page could not be read. */
  readonly wallFailed: boolean;
  /** The client list has no answer yet. It stays up across a switch of client: it is the same list. */
  readonly listPending: boolean;
  /** No read has named the clients, and the one that should have failed. */
  readonly listFailed: boolean;
  /**
   * The narrowed client is not one this trainer coaches (a 404, R27): an old link, or a client whose
   * coaching ended. The screen drops `?klient=` and shows every client.
   */
  readonly clientGone: boolean;
  readonly retry: () => void;
  readonly older: OlderPages;
  readonly view: RecordingsView;
}

/** `/nagrania`'s one read, a page at a time, and its projection. */
export function useRecordings(customerId: number | null): RecordingsState {
  const { t, i18n } = useTranslation();
  const lang = useContentLanguage();
  const viewer = useSessionUser();
  const collection = useClipCollection(customerId);
  // The whole collection's answer, from the cache and never fetched here: where a narrowed read
  // failed, its client list is still the one to show.
  const everyone = useInfiniteQuery({ ...clipCollectionQueryOptions(null, lang), enabled: false });

  const clientGone = customerId !== null && collection.failed && isGone(collection.error);
  const clients = collection.clients ?? everyone.data?.pages[0]?.clients ?? null;

  return {
    wallPending: collection.pending || collection.stale || clientGone,
    wallFailed: collection.failed && !clientGone,
    listPending: clients === null && (collection.pending || clientGone),
    listFailed: clients === null && collection.failed && !clientGone,
    clientGone,
    retry: collection.retry,
    older: collection.older,
    view: buildRecordings(
      { pages: collection.pages, clients, customerId, viewerUserId: viewer?.id ?? null },
      { t, locale: i18n.language, now: new Date() },
    ),
  };
}
